/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { CantonsViz } from 'components/CantonsViz'
import { CategoryViz } from 'components/CategoryViz'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import GetSheetDone from 'get-sheet-done'
import { extent } from 'd3-array'
import { KeyTable } from 'components/KeyTable'
import { Box } from 'components/Box'
import { Tooltip } from 'components/Tooltip'
import { LOCALES_DE, TEXT_DE } from '../locales_de'
import { LOCALES_FR, TEXT_FR } from '../locales_fr'
import { useWindowSize } from 'hooks/useWindowSize'
import { INDICATORS, color } from '../globals'
import { csvParse } from 'd3-dsv'
import { tenantcantons, tenantlanguage } from 'components/tenants'
//
//
//
// DEPLOY
// 1. Change DEFAULT_LOCALE -> DE || FR

// 2. Copy content of .taStandalonerc_de.yml || .taStandalonerc_fr.yml in .taStandalonerc.yml
// 3. Copy content of .gitlab-ci_de.yml || .gitlab-ci_fr.yml in .gitlab-ci.yml
// 4. Push to Production
// 5. Wait till pipeline has finished before deploy othe language
//
//
//
const DEFAULT_LOCALE = 'DE'
//
const TENANT = window.location.hostname.split('.')[1]
const LOCALE: localeType =
  (tenantlanguage[TENANT] as localeType) || DEFAULT_LOCALE

const TENANT_CANTON_ID = tenantcantons[TENANT] || ''
const TEXT = LOCALE === 'DE' ? TEXT_DE : TEXT_FR
const LOCALES = LOCALE === 'DE' ? LOCALES_DE : LOCALES_FR
const ARTICLEID_DE = '919599751528'
const ARTICLEID_FR = '710704955119'
const ARTICLE_ID = LOCALE === 'DE' ? ARTICLEID_DE : ARTICLEID_FR
// TODO Set the correct article ID here

export function AppComponent() {
  const [indicators, setIndicators] = useState<
    indicatorDataType[] | undefined
  >()
  const [drawdata, setDrawdata] = useState<drawdataType[] | undefined>()
  const [keydata, setKeyData] = useState<vizElementDataType[] | undefined>()
  const [hoveredElement, setHoveredElement] = useState()
  const size = useWindowSize()
  const [showAll, setShowAll] = useState(window.innerWidth < 750 ? false : true)
  const SHEET_URL =
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vQSHWyAnA55iMNyAH-VlUgrcS6mhnq742f2ReiMVf1DkeQ9MKdUXmK-3lAEd9USGQrKVdeZuCvmyYQf/pub?gid=254077302&single=true&output=csv'
  const CANTONS_PER_ROW = size.width < 750 ? 3 : size.width < 1000 ? 5 : 6

  useEffect(() => {
    fetch(SHEET_URL)
      .then(d => d.text())
      .then(d => {
        const sheetdata: { [key: string]: string }[] = csvParse(d)
        const directions = sheetdata.shift() || {}
        const key_de = sheetdata.shift() || {}
        const key_fr = sheetdata.shift() || {}
        const names_de = sheetdata.shift() || {}
        const units_de = sheetdata.shift() || {}
        const names_fr = sheetdata.shift() || {}
        const units_fr = sheetdata.shift() || {}

        const indicators = INDICATORS.map(id => {
          return {
            id: id as indicatorTypes,
            dir: directions[id] as directionType,
            key_de: key_de[id],
            key_fr: key_fr[id],
            name_de: names_de[id],
            unit_de: units_de[id],
            name_fr: names_fr[id],
            unit_fr: units_fr[id],
            minmax: extent(sheetdata.map(canton => +canton[id]))
          }
        })
        setIndicators(indicators)

        const formated = sheetdata.map(canton => {
          const { namede, namefr, code, rank } = canton
          const data = {} as dataElementCollection
          indicators.map(indicator => {
            data[indicator.id] =
              canton[indicator.id] !== ''
                ? {
                    abs: +canton[indicator.id],
                    norm: canton[indicator.id + 'norm']
                      ? +canton[indicator.id + 'norm']
                      : undefined,
                    color: color(
                      +canton[indicator.id + 'norm'],
                      indicator.dir,
                      sheetdata.map(canton =>
                        +canton[indicator.id + 'norm'] === 0
                          ? 0.00001
                          : +canton[indicator.id + 'norm']
                      )
                    )
                  }
                : {}
          })
          return { namede, namefr, code, data, rank }
        })

        setDrawdata(formated.sort((a, b) => +a.rank - +b.rank))
        const keyCantons = formated.filter(
          f => LOCALES.keycantons.indexOf(f.code) !== -1
        )

        if (keyCantons) {
          setKeyData(
            keyCantons.map(m => {
              const cols = {} as { [K in indicatorTypes]: string }
              indicators.forEach(ind => {
                cols[ind.id] = m.data[ind.id].color || '#000'
              })
              return {
                label: LOCALE === 'DE' ? m?.namede : m?.namefr,
                id: m.code,
                colors: cols
              }
            })
          )
        }
      })
  }, [])

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <header className='ui vertical very fitted segment'>
        <div className='ui container'>
          <div className='ui header'>
            <div className='ta kicker'>{LOCALES.kicker}</div>

            <h1>{TEXT.title}</h1>
            <h2>{TEXT.lead}</h2>
          </div>
        </div>
      </header>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>{LOCALES.authors}</div>
            <div className='ta updated'>{LOCALES.updated}</div>
          </div>
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>{TEXT && TEXT.intro.text}</div>
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 style={{ marginTop: '1em' }}>{TEXT && TEXT.result1.title}</h4>

            {TEXT && TEXT.result1.text}
          </div>
        </div>

        <div className='ui container'>
          {keydata && indicators && (
            <KeyTable
              data={keydata}
              locales={indicators.reduce((a, b) => {
                a[b.id] = LOCALE === 'DE' ? b.key_de : b.key_fr
                return a
              }, {} as { [K in indicatorTypes]: string })}
              colorLabels={LOCALES.key}
              isMobile={size.width < 750}
            />
          )}
        </div>
        <div className='ui text container'>
          <div className='ta text'>{TEXT && TEXT.result2.text}</div>
        </div>
      </div>
      <div className='ui vertical  segment'>
        <div className='ui container'>
          <div className='ta text'>
            <div className='mainvizcontainer'>
              {drawdata &&
                indicators &&
                drawdata
                  .map(canton => {
                    const colors = {} as { [K in indicatorTypes]: string }
                    indicators.forEach(ind => {
                      colors[ind.id as indicatorTypes] =
                        canton.data[ind.id as indicatorTypes].color || '#000'
                    })
                    return {
                      label: LOCALE === 'FR' ? canton.namefr : canton.namede,
                      id: canton.code,
                      colors
                    }
                  })
                  // .slice(0, showAll ? drawdata.length : 15)
                  .map(m => {
                    return (
                      <CantonsViz
                        elementsPerRow={CANTONS_PER_ROW}
                        data={m}
                        setHoveredElement={setHoveredElement}
                        isImportant={m.id === TENANT_CANTON_ID}
                      />
                    )
                  })}
              {drawdata && hoveredElement && indicators && (
                <Tooltip
                  data={drawdata.find(f => f.code === hoveredElement.code)}
                  pos={hoveredElement.pos}
                  indicators={indicators}
                  nodataLabel={LOCALES.nodata}
                  locale={LOCALE}
                />
              )}
              {/* {size.width < 750 && (
                <button
                  className='showAll'
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll
                    ? LOCALES.showAllButton[1]
                    : LOCALES.showAllButton[0]}
                </button>
              )} */}
            </div>
          </div>
        </div>
        <div className='ui text container'>
          <div className='ta text'>{TEXT && TEXT.result3.text}</div>
        </div>
      </div>
      {indicators &&
        indicators.map(indicator => {
          return (
            <div className='ui vertical  segment'>
              <div className='ui text container'>
                <div className='bigIconContainer'>
                  <img
                    className='bigIcon'
                    src={require('images/icons_big_' + indicator.id + '.svg')}
                  />
                </div>
                <div
                  className='ta text'
                  style={{ textAlign: 'center', marginBottom: '1em' }}
                >
                  {TEXT && <h3>{TEXT[indicator.id].title}</h3>}
                </div>
              </div>
              <div className='ui  container'>
                {drawdata && (
                  <CategoryViz
                    isMobile={size.width < 750}
                    data={drawdata.map(canton => {
                      return {
                        code: canton.code,
                        label: LOCALE === 'DE' ? canton.namede : canton.namefr,
                        value: canton.data[indicator.id as indicatorTypes]
                      }
                    })}
                    dir={indicator.dir}
                    name={
                      LOCALE === 'DE' ? indicator.name_de : indicator.name_fr
                    }
                    unit={
                      LOCALE === 'DE' ? indicator.unit_de : indicator.unit_fr
                    }
                    tenant={TENANT_CANTON_ID}
                  />
                )}
              </div>
              <div className='ui text container'>
                <div className='ta text'>
                  {TEXT && <div>{TEXT[indicator.id].text}</div>}
                </div>
              </div>
            </div>
          )
        })}
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 style={{ marginTop: '1em' }}>{TEXT && TEXT.fazit.title}</h4>
            {TEXT && TEXT.fazit.text}
          </div>
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <Box title={TEXT.method.title}>{TEXT.method.text}</Box>
          </div>
        </div>
      </div>
      <Footer
        articleId={ARTICLE_ID}
        questions={LOCALES.questions}
        hidePortfolioTeaser={LOCALE === 'FR'}
        credits={[
          [
            LOCALES.credits[0],
            'Simone Luchetta, Titus Plattner, Patrick Meier'
          ],
          [LOCALES.credits[1], 'Patrick Meier, Sebastian Broschinski']
        ]}
        locale={LOCALE === 'DE' ? 'de' : 'fr'}
      />
    </div>
  )
}
